import { can } from '@af/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Checkbox, Field, Textarea } from '@/components/ui/input';
import { ApiClientError, call } from '@/lib/api';
import { MAX_BROADCAST, planBroadcast } from '@/lib/broadcast';
import { useRole } from '@/lib/permissions';
import { qk, useAgents, useOrgStatus } from '@/lib/queries';
import { liveRunStore } from '@/lib/run-resume';
import { isChatPane, useWorkspace } from './store';

/**
 * One message to several agents at once. Each agent gets it in the conversation you already have open with
 * them (or a new one), and every answer streams in its own pane. No LLM involved in the routing.
 */
export function BroadcastDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const role = useRole();
  const status = useOrgStatus();
  const ws = useWorkspace();
  const agents = useAgents();
  const [selected, setSelected] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const runnable = useMemo(() => (agents.data ?? []).filter((a) => a.status === 'active'), [agents.data]);
  const openAgentIds = useMemo(
    () => [
      ...new Set(
        ws.state.panes
          .filter(isChatPane)
          .map((k) => ws.state.meta[k]?.agentId)
          .filter((id): id is string => !!id),
      ),
    ],
    [ws.state.panes, ws.state.meta],
  );

  // Start from the agents you are already talking to.
  // biome-ignore lint/correctness/useExhaustiveDependencies: preselect once per opening
  useEffect(() => {
    if (open) setSelected(openAgentIds.slice(0, MAX_BROADCAST));
  }, [open]);

  const toggle = (id: string, on: boolean) =>
    setSelected((s) => (on ? (s.length < MAX_BROADCAST ? [...s, id] : s) : s.filter((x) => x !== id)));

  const submit = async () => {
    const message = text.trim();
    if (!message || selected.length === 0 || busy) return;
    if (status.data?.agentsHalted) return void toast.error(t('home.halted'));
    if (!can(role, 'conversation:create') || !can(role, 'agent:run'))
      return void toast.error(t('home.readOnly'));
    setBusy(true);
    const panes = ws.state.panes.map((key) => ({ key, agentId: ws.state.meta[key]?.agentId }));
    const targets = planBroadcast(selected, panes, ws.state.focused);
    const results = await Promise.all(
      targets.map(async (target) => {
        const name = agents.data?.find((a) => a.id === target.agentId)?.name ?? target.agentId;
        try {
          let conversationId = target.conversationId;
          let sent: { runId: string } | null = null;
          if (conversationId) {
            try {
              sent = await call('sendMessage', { params: { id: conversationId }, body: { text: message } });
            } catch (err) {
              // That conversation is still answering: talk to the agent in a fresh one instead.
              if (!(err instanceof ApiClientError) || err.status !== 409) throw err;
              conversationId = undefined;
            }
          }
          if (!conversationId) {
            const c = await call('createConversation', {
              body: { agentId: target.agentId, title: message.slice(0, 48) },
            });
            conversationId = c.id;
            sent = await call('sendMessage', { params: { id: c.id }, body: { text: message } });
          }
          liveRunStore.set(conversationId, sent!.runId);
          void qc.invalidateQueries({ queryKey: qk.messages(conversationId) });
          return { name, conversationId };
        } catch (err) {
          toast.error(`${name}: ${err instanceof Error ? err.message : String(err)}`);
          return null;
        }
      }),
    );
    setBusy(false);
    void qc.invalidateQueries({ queryKey: ['conversations'] });
    const ok = results.filter((r): r is { name: string; conversationId: string } => !!r);
    if (ok.length === 0) return;
    for (const r of ok) ws.open(`chat:${r.conversationId}`);
    const last = ok[ok.length - 1]!;
    void navigate({ to: '/chat/$conversationId', params: { conversationId: last.conversationId } });
    setText('');
    onOpenChange(false);
    toast.success(t('broadcast.sent', { count: ok.length }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t('broadcast.title')}
        description={t('broadcast.description', { max: MAX_BROADCAST })}
        footer={
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!text.trim() || selected.length === 0}
              onClick={() => void submit()}
            >
              {t('broadcast.send', { count: selected.length })}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label={t('broadcast.agents')}>
            <ul className="grid max-h-48 gap-1.5 overflow-y-auto sm:grid-cols-2">
              {runnable.map((a) => {
                const checked = selected.includes(a.id);
                return (
                  <li key={a.id}>
                    <Checkbox
                      id={`bc-${a.id}`}
                      checked={checked}
                      disabled={!checked && selected.length >= MAX_BROADCAST}
                      onChange={(v) => toggle(a.id, v)}
                      label={
                        <span className="min-w-0 truncate">
                          {a.name} <span className="text-muted">· {a.roleTitle}</span>
                        </span>
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </Field>
          <Field label={t('broadcast.message')}>
            <Textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder={t('broadcast.placeholder')}
            />
          </Field>
        </div>
      </DialogContent>
    </Dialog>
  );
}
